import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import DeliveryMode from '../../store/deliveryMode';
import LoaderStore from '../../store/loader';
import { ActiveProduct, CalledSuperProductCategories } from '../../store/products';
import ProductsCollection from '../../store/productsCollection';
import { earlyInUseEffect } from '../../utils';
import { getProductsCartInfo, viewItemList } from '../../utils/analytics';

import ProductList from './ProductList';
import ProductListItem from './ProductListItem';
import styles from './ProductMenu.module.scss';

export default function SmartProductMenu({
  slug,
  categories,
  category,
  subCategory,
  productCollections,
  banners,
  isSticky,
  showBySelection,
  isSecondLevelPage,
  staticProducts = {},
}) {
  const [productsShowing, setProductsShowing] = useState({});
  const [categoriesShowing, setCategoriesShowing] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [infiniteScrollIndex, setInfiniteScrollIndex] = useState(1);
  const [allProductsInCollections, setAllProductsInCollections] = useState();
  const [loading, setLoading] = useState(true);

  function findProductsSubcategory(subcategory, _products, isOffers, isSuper = false) {
    if (!_products || _products?.length === 0) {
      return 0;
    }
    let allProductsToShow = [];
    if (subcategory.subCategories.length !== 0) {
      const productsInCategory = _products.filter(p => subcategory._id === p?.category?.categoryTree?.parent);
      const productsToShow = isOffers
        ? productsInCategory.filter(p => p.offerType !== 'none' && p.offerType !== 'super')
        : isSuper
          ? productsInCategory.filter(p => p.offerType === 'super')
          : productsInCategory;

      allProductsToShow = [...allProductsToShow, ...productsToShow];

      setProductsShowing(prevState => ({
        ...prevState,
        [subcategory._id]: prevState[subcategory._id]
          ? [...prevState[subcategory._id], ...productsToShow]
          : productsToShow,
      }));
    }
    const productsInCategory = _products.filter(p => subcategory._id === p?.category?._id);

    const productsToShow = isOffers
      ? productsInCategory.filter(p => p.offerType !== 'none' && p.offerType !== 'super')
      : isSuper
        ? productsInCategory.filter(p => p.offerType === 'super')
        : productsInCategory;
    allProductsToShow = [...allProductsToShow, ...productsToShow];

    setProductsShowing(prevState => ({
      ...prevState,
      [subcategory._id]: prevState[subcategory._id]
        ? [...prevState[subcategory._id], ...productsToShow]
        : productsToShow,
    }));
    return allProductsToShow.length;
  }

  function getProductsToShow(currentCategory, products, isOffers = false, isSuper = false) {
    if (!currentCategory) return;
    const count = findProductsSubcategory(currentCategory, products, isOffers, isSuper);
    setLoading(false);
    return count > 0;
  }

  function loadFunc() {
    if (!categories || !categories.length) return;
    if (categoriesShowing.length && !categoriesShowing[infiniteScrollIndex]) {
      setHasMore(false);
      return;
    }
    if (hasMore && categoriesShowing.length) {
      const _category = categoriesShowing[infiniteScrollIndex];
      const isSuper = slug[0] === 'smart';
      getProductsToShow(_category, allProductsInCollections, false, isSuper);
      setInfiniteScrollIndex(infiniteScrollIndex + 1);
      CalledSuperProductCategories.update(infiniteScrollIndex + 1);
    }
  }

  function initialLoadFunc(index, _allProductsInCollections, allSubCategories) {
    if (!categories || !categories.length) return;
    if (hasMore && allSubCategories.length) {
      const _category = allSubCategories[index];
      const isSuper = slug[0] === 'smart';
      getProductsToShow(_category, _allProductsInCollections, false, isSuper);
    }
  }

  function scrollToActiveProduct() {
    setTimeout(() => {
      const elemToScrollTo = document.getElementById(`pr${ActiveProduct.getValue()}`);
      if (elemToScrollTo) {
        elemToScrollTo.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
        ActiveProduct.update('');
        LoaderStore.removeEvent('SMART_SCROLL');
      } else {
        LoaderStore.removeEvent('SMART_SCROLL');

        ActiveProduct.update('');
        CalledSuperProductCategories.update(1);
      }
    }, 500);
  }

  useEffect(() => {
    if (!slug) return earlyInUseEffect;
    if (!categories || !categories.length) return earlyInUseEffect;
    if (!productCollections) return earlyInUseEffect;
    if (slug[0] === 'smart') {
      const service = DeliveryMode.getValue();
      const collectionType = DeliveryMode.getCollectionAsNumber(service) || [];
      const mainCollectionType = collectionType.find(c => c === process.env.defaultCollectionType || c === '751');
      if (!mainCollectionType) return;
      const _allProductsInCollections = ProductsCollection.getProductsForCollections(collectionType);
      setAllProductsInCollections(_allProductsInCollections);
      setProductsShowing({});

      setInfiniteScrollIndex(1);

      const [categorySlug] = slug;
      if (categorySlug === 'smart') {
        const allSubCategories = categories.flatMap(c => c.subCategories);
        setHasMore(true);
        setCategoriesShowing(allSubCategories);
        let localIndex = 0;
        let hasProd = false;
        if (CalledSuperProductCategories.getValue() === 1 || ActiveProduct.getValue() === '') {
          while (localIndex < allSubCategories.length && !hasProd) {
            if (allSubCategories.length) {
              const _category = allSubCategories[localIndex];
              const isSuper = slug[0] === 'smart';
              hasProd = getProductsToShow(_category, _allProductsInCollections, false, isSuper);
              setInfiniteScrollIndex(localIndex + 1);
            }

            localIndex = localIndex + 1;
          }
        }
        if (CalledSuperProductCategories.getValue() !== 1 && ActiveProduct.getValue() !== '') {
          LoaderStore.addEvent('SMART_SCROLL');
          for (let i = 0; i < CalledSuperProductCategories.getValue(); i++) {
            initialLoadFunc(i, _allProductsInCollections, allSubCategories);
          }
          setInfiniteScrollIndex(CalledSuperProductCategories.getValue());
          scrollToActiveProduct();
        } else {
          CalledSuperProductCategories.update(1);
        }
      }
    }
  }, [categories, productCollections]);

  const productShowingLength = Object.keys(productsShowing).length;

  useEffect(() => {
    // send analytics view_item_list for every new category added to the viewport
    if (productShowingLength > 0) {
      const lastCategory = Object.keys(productsShowing)[productShowingLength - 1];

      viewItemList(getProductsCartInfo(productsShowing[lastCategory]), {
        item_list_id: category?._id,
        item_list_name: category?.name,
      });
    }
  }, [productShowingLength]);

  // Code for bots
  if (isEmpty(categoriesShowing)) {
    return (
      <section className={styles.visibilityHidden}>
        <div className={styles.listHeader}>
          <h1 className={styles.listTitle}>{isEmpty(subCategory) ? category?.name : subCategory?.name}</h1>
        </div>
        {Object.values(staticProducts).map((productsArray, index) => (
          <div key={Object.keys(staticProducts)[index]}>
            {isEmpty(subCategory) && (
              <div className={styles.listHeader}>
                <h2 className={styles.listTitle}>{category?.subCategories?.[index]?.name}</h2>
              </div>
            )}
            {productsArray.map((product, pIndex) => (
              <ProductListItem
                key={product._id}
                _product={product}
                itemListName={isEmpty(subCategory) ? category?.name : subCategory?.name}
                itemListId={isEmpty(subCategory) ? category?.name : subCategory?.name}
                positionInList={pIndex + 1}
              />
            ))}
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className={styles.listContainer}>
      <div className={cn(styles.infiniteList, { [styles.notSticky]: !isSticky })}>
        {categoriesShowing && categoriesShowing.length !== 0 && Object.values(productsShowing).length !== 0 && (
          <InfiniteScroll
            threshold={440} // 440px is the height of a category with 1 row of products
            hasMore={hasMore}
            // keep it like this until we purge the logic of multiple async calls to local db for products
            initialLoad={false}
            loader={<div key="0" />}
            loadMore={loadFunc}
          >
            {Object.values(productsShowing).map((products, i) => (
              <ProductList
                key={categoriesShowing[i]._id}
                products={products}
                category={categoriesShowing[i]}
                categoriesShowing={categoriesShowing}
                slug={slug}
                banners={banners}
                showBySelection={showBySelection}
                isSecondLevelPage={isSecondLevelPage}
                categories={categories}
                loading={loading}
                itemListId={category?._id}
                itemListName={category?.name}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </section>
  );
}
